import C from "@/modules/dashboard/Dashboard.module.css";

import { Badge } from "@/components/Badge.tsx";
import { SeparatedBy } from "@/components/SeparatedBy.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { getResourceIconClass, getResourceLink, getResourceTypeName } from "@/modules/api/resourceTypes.tsx";
import { allResourcesAtom, type RecentResource } from "@/modules/dashboard/recentQuery.ts";
import { DiskStatusBadge } from "@/modules/disks/DiskStatusBadge.tsx";
import { ErrorRow } from "@/modules/error/ErrorFallback.tsx";
import { ReportingErrorBoundary } from "@/modules/error/ReportingErrorBoundary.tsx";
import { formatDistanceAtom } from "@/modules/l10n/dateFnsLocale.ts";
import { MetalStatusBadge } from "@/modules/metal/status/MetalStatus.tsx";
import { ResourceStatusBadge } from "@/modules/resource/ResourceStatusBadge.tsx";
import { getResourceName } from "@/modules/resource/getResourceName.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { useAtomValue } from "jotai/react";
import { Suspense, type ReactNode } from "react";
import { sortBy } from "remeda";

function RecentItem({ item }: { item: RecentResource }) {
    const action = getResourceLink(item);
    const formatDistance = useAtomValue(formatDistanceAtom);

    return (
        <Link className={cn(C.ListItem, C.Hoverable)} {...action}>
            <div className={C.Icon}>
                <MaskIcon className={cn("size-1.25rem text-primary", getResourceIconClass(item.$type))} />
            </div>

            <div className={C.Content}>
                {getResourceName(item)}
                <div className="color-muted font-size-small">
                    {item.created_at
                        ? formatDistance(apiDatetimeToDate(item.created_at), new Date(), {
                              addSuffix: true,
                          })
                        : "-"}
                    <span className="inline-block px-1">&bull;</span>
                    {getResourceTypeName(item.$type)}
                </div>
            </div>

            <div className={C.Status}>
                <ResourceStatus item={item} />
            </div>

            <div className={C.Btn}>
                <MaskIcon className="jp-arrow-thin-right-icon size-0.75rem" />
            </div>
        </Link>
    );
}

export interface RecentlyModifiedProps {
    items: ReactNode[] | undefined;
    error?: unknown;
    isPending: boolean;
}

function RecentlyModifiedLayout({ items, error, isPending }: RecentlyModifiedProps) {
    const errorElement = error ? <ErrorRow error={error} tags="RecentlyModified" /> : null;
    const limit = 10;
    return (
        <div className={C.Modified}>
            <div className={cn(C.BlockTitle, "pl-2")}>
                <h2>
                    <Trans>Recent</Trans>
                </h2>
            </div>

            <div className={C.Block}>
                {items?.length || isPending || !!errorElement ? (
                    <div className={C.Scroll}>
                        {items}

                        {errorElement}

                        {isPending && <Loading icon="none" delay={0} />}

                        {items != null && items.length > 9 ? (
                            <div className={C.ListItem}>
                                <div className="text-muted">
                                    <Trans comment="An item is any kind of resource being managed (e.g. VM, disk, bucket, etc).">
                                        Showing {limit} latest items
                                    </Trans>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className={cn(C.Empty, "color-muted")}>
                        <div>
                            <MaskIcon className="jp-cactus-icon size-4rem" />
                            <p className="pt-2">
                                <Trans>Take some action and you'll never see this cactus again.</Trans>
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export interface RecentlyModifiedPropsLoaderProps extends Omit<
    RecentlyModifiedProps,
    "items" | "isPending" | "error"
> {}

function RecentlyModifiedLoader({ ...props }: RecentlyModifiedPropsLoaderProps) {
    //#region Hooks
    const { data: allItems, error, isPending } = useAtomValue(allResourcesAtom);
    //#endregion

    const items = sortBy(allItems, [(item) => apiDatetimeToDate(item.created_at).getTime(), "desc"]).slice(0, 10);

    const rows = items.map((item: RecentResource, index: number) => (
        // TODO: Use proper `key` (create a `getResourceKey()` or similar)
        <RecentItem key={index} item={item} />
    ));
    return <RecentlyModifiedLayout {...props} items={rows} error={error} isPending={isPending} />;
}

export function RecentlyModified(props: RecentlyModifiedPropsLoaderProps) {
    return (
        <ReportingErrorBoundary
            FallbackComponent={({ error }: { error: unknown }) => (
                <RecentlyModifiedLayout {...props} items={undefined} error={error} isPending={false} />
            )}
            tags="RecentlyModified"
        >
            <Suspense fallback={<RecentlyModifiedLayout {...props} items={undefined} isPending={true} />}>
                <RecentlyModifiedLoader {...props} />
            </Suspense>
        </ReportingErrorBoundary>
    );
}

function ResourceStatus({ item }: { item: RecentResource }) {
    const { $type } = item;
    switch ($type) {
        case "api_token":
            return null;

        case "access_delegation": {
            let acceptedSum = 0;
            let pendingSum = 0;

            for (const s of item.impersonations) {
                if (s.accepted_at) {
                    acceptedSum++;
                } else {
                    pendingSum++;
                }
            }

            return acceptedSum || pendingSum ? (
                <SeparatedBy separator=" ">
                    {!!acceptedSum && (
                        <Badge color="success">
                            {acceptedSum} <Trans>Accepted</Trans>
                        </Badge>
                    )}
                    {!!pendingSum && (
                        <Badge color="warning">
                            {pendingSum} <Trans>Pending</Trans>
                        </Badge>
                    )}
                </SeparatedBy>
            ) : null;
        }
        case "access_impersonation":
            // TODO: Re-use text/translation/code from existing Delegation components
            return item.is_accepted ? (
                <Badge color="success">
                    <Trans>Accepted</Trans>
                </Badge>
            ) : (
                <Badge color="warning">
                    <Trans>Pending</Trans>
                </Badge>
            );

        case "ip_address":
            // TODO: Re-use text/translation/code from existing Network components
            return item.assigned_to ? (
                <Badge color="success">
                    <Trans>Assigned</Trans>
                </Badge>
            ) : (
                <Badge color="error">
                    <Trans>Unassigned</Trans>
                </Badge>
            );

        case "load_balancer":
            return null;

        case "virtual_machine":
        case "managed_service":
            return <ResourceStatusBadge item={item} />;

        case "vpc":
        case "bucket":
        case "firewall":
            return undefined;

        case "bare_metal":
            return <MetalStatusBadge value={item.status} />;

        case "disk":
            return <DiskStatusBadge value={item.status} />;

        default:
            exhaustiveSwitchCheck($type);
    }
}
